// src/pages/Home.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { FaUser, FaLock, FaSpinner } from "react-icons/fa";
import logo from "./business-logo.png";
import { api } from "../services/api";
import "./Home.css";

export default function Home() {
  const nav = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(""); // 2FA Token
  const [requires2fa, setRequires2fa] = useState(false);
  const [notice, setNotice] = useState({ type: null, text: "" });
  const [loading, setLoading] = useState(false);

  const canSubmit = (username.trim() && password) || (requires2fa && token.length === 6);

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || loading) return;
    setNotice({ type: null, text: "" });
    setLoading(true);

    try {
      // --- 1. If 2FA is required, validate token ---
      if (requires2fa) {
        const res = await api.validate2FALogin(token);

        // Redirect based on role
        if (res.user?.role === 'admin') {
          window.location.href = '/admin';
        } else {
          window.location.href = '/staff';
        }
        return;
      }

      // --- 2. Try Admin Login ---
      try {
        const res = await api.adminLogin(username.trim(), password);
        if (res.requires2fa) {
          setRequires2fa(true);
          setLoading(false);
          return;
        }
        nav("/admin");
        return;
      } catch (adminErr) {
        // Fallthrough to Staff Login
      }

      // --- 3. Try Staff Login ---
      try {
        const res = await api.staffLogin(username.trim(), password);
        if (res.requires2fa) {
          setRequires2fa(true);
          setLoading(false);
          return;
        }
        nav("/staff");
      } catch (staffErr) {
        setNotice({ type: "error", text: "Invalid username or password." });
      }

    } catch (err) {
      console.error(err);
      setNotice({ type: "error", text: err.message || "An error occurred." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="loginPage">
      <div className="loginWrap">
        <img src={logo} alt="Heston Automotive" className="loginLogo" />
        <h1 className="loginHeading">Sign In To Your Appraisal Account</h1>

        <form onSubmit={submit} className="loginForm">
          {!requires2fa ? (
            <>
              <div className="field">
                <div className="fieldIcon">
                  <FaUser />
                </div>
                <input
                  type="text"
                  name="username"
                  autoComplete="username"
                  placeholder="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="inputMinimal"
                  required
                />
              </div>

              <div className="field">
                <div className="fieldIcon">
                  <FaLock />
                </div>
                <input
                  type="password"
                  name="password"
                  autoComplete="current-password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="inputMinimal"
                  required
                />
              </div>
            </>
          ) : (
            <div className="field">
              <div className="fieldIcon">
                <FaLock />
              </div>
              <input
                type="text"
                name="token"
                placeholder="Enter verification code from email"
                value={token}
                onChange={(e) => setToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="inputMinimal text-center tracking-widest text-lg"
                autoFocus
                required
              />
            </div>
          )}

          {notice.type === "error" && (
            <div role="alert" className="alertMinimal">
              {notice.text}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            className={`btnMinimal ${!canSubmit ? "disabled" : ""}`}
          >
            {loading ? <FaSpinner className="spin" /> : (requires2fa ? "Verify Code" : "Login")}
          </button>
        </form>

        <div className="loginLinks">
          <Link to="/forgot-password">Forgotten password?</Link>
        </div>

        <div className="watermark">
          <span className="markDot" />
          Heston Automotive
          <div className="copyright">© 2025</div>
        </div>
      </div>
    </div>
  );
}
